import type { LucideIcon } from "lucide-react";
import { Compass } from "lucide-react";
import {
  sigilPillars,
  type PillarCode,
  type SigilPillar,
} from "~/app/data/sigilPillars";

export interface SidebarNavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Pillar this row is filed under in the nav (LS / LM / LC / LD); omitted for the overview hub. */
  pillar?: PillarCode;
  /** Part of the v1.0 MVP cut per rebuild strategy §4. */
  isMvp?: boolean;
  /**
   * Source still lives under `src/_archive_v0/` and is not in the route tree.
   * Render with a plain `<a>` and a "coming v1.x" affordance.
   */
  isArchived?: boolean;
  /** Shown when expanded; global palette uses filter row ⌘K instead. */
  shortcutHint?: string;
}

export interface SidebarNavGroup {
  id: string;
  label: string;
  pillar: PillarCode;
  items: readonly SidebarNavItem[];
}

export const sidebarOverviewItem: SidebarNavItem = {
  to: "/",
  label: "Overview",
  icon: Compass,
  isMvp: true,
};

const groupIdForPillar: Record<PillarCode, string> = {
  LS: "ls-style",
  LM: "lm-motion",
  LC: "lc-components",
  LD: "ld-design",
};

const groupLabelForPillar: Record<PillarCode, string> = {
  LS: "LS · Style",
  LM: "LM · Motion",
  LC: "LC · Components",
  LD: "LD · Design",
};

function pillarToGroup(pillar: SigilPillar): SidebarNavGroup {
  const items: SidebarNavItem[] = pillar.links
    .filter((link) => link.isPrimary)
    .map((link) => ({
      to: link.to,
      label: link.label,
      icon: link.icon,
      pillar: pillar.code,
      isMvp: link.isMvp,
      isArchived: link.isArchived,
    }));
  return {
    id: groupIdForPillar[pillar.code],
    label: groupLabelForPillar[pillar.code],
    pillar: pillar.code,
    items,
  };
}

/**
 * 4-SIGILS taxonomy nav: one group per pillar, in canonical order LS → LM →
 * LC → LD. Each route appears under exactly one pillar (its `isPrimary`
 * owner) so shared routes like `/colors` aren't duplicated in the sidebar
 * even though the overview hub still renders them under both LS and LD.
 */
export const sidebarNavGroups: readonly SidebarNavGroup[] = sigilPillars.map(
  pillarToGroup,
);

export function itemMatchesFilter(
  item: SidebarNavItem,
  query: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (q === "") return true;
  const pathKey = item.to.replace(/^\//, "").toLowerCase();
  return (
    item.label.toLowerCase().includes(q) ||
    pathKey.includes(q) ||
    item.to.toLowerCase().includes(q) ||
    (item.pillar?.toLowerCase().includes(q) ?? false)
  );
}
